import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle2, ArrowRight, ExternalLink, Mail, Check } from 'lucide-react';
import DashboardLayout from '../../layouts/DashboardLayout';

interface RegistrationData {
  teamName: string;
  teamLeader: { name: string; email: string };
  leaderGrade: string;
  leaderJobRole: string;
  teamMembers: Array<{
    id: string;
    name: string;
    email: string;
    grade: string;
    jobRole: string;
  }>;
}

export default function RegistrationSuccess() {
  const navigate = useNavigate();
  const [data, setData] = useState<RegistrationData | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('registrationData');
    if (raw) {
      setData(JSON.parse(raw));
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (!data) return null;

  const totalMembers = data.teamMembers.length + 1;
  const regId = 'CAP-2026-QHRBEH02';

  return (
     <DashboardLayout>
    <div className="min-h-screen bg-gradient-to-b from-[#e3f2fd] via-[#ebf5fb] to-[#e1f5fe] font-['Inter',sans-serif]">
      {/* Top Navigation Bar */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-black/10 shadow-[0px_4px_6px_0px_rgba(0,0,0,0.05)] px-4 sm:px-6 h-16 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-[10px] bg-gradient-to-b from-[#0058ab] to-[#1db8f2] shadow-[0px_10px_15px_0px_rgba(0,88,171,0.2)] flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M9.33333 1.33333L2 9.33333H8L7.33333 14.6667L14.6667 6.66667H8.66667L9.33333 1.33333Z" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="text-[#121a38] text-sm font-semibold hidden sm:inline">Innovation Hackathon 2026</span>
        </div>

        <div className="flex items-center gap-2">
          <CheckCircle2 className="size-4 text-[#00A35C]" />
          <span className="text-[#00A35C] text-sm font-semibold">Registration Complete</span>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white/70 border-b border-black/10 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1)] py-10 sm:py-14 text-center relative overflow-hidden">
        {/* Success Icon */}
        <div className="flex justify-center mb-4">
          <div className="size-16 rounded-full flex items-center justify-center" style={{ backgroundImage: 'linear-gradient(135deg, rgba(0,163,92,0.1) 0%, rgba(0,213,208,0.1) 100%)' }}>
            <div className="border border-[rgba(0,163,92,0.2)] rounded-full absolute inset-0" style={{ position: 'relative', width: 64, height: 64 }} />
            <CheckCircle2 className="size-8 text-[#00A35C]" strokeWidth={2} />
          </div>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[rgba(0,163,92,0.05)] border border-[rgba(0,163,92,0.3)] rounded-full px-4 py-1.5 mb-4">
          <div className="size-1.5 rounded-full bg-[#00A35C]" />
          <span className="text-[#00A35C] text-xs font-semibold">Successfully Registered</span>
        </div>

        <h1 className="text-[#121a38] text-[28px] font-bold leading-[34px] mb-3">
          Your Team is In! 🎉
        </h1>
        <p className="text-sm max-w-md mx-auto px-4">
          <span className="text-[#0058ab] font-semibold">{data.teamName}</span>
          <span className="text-[#6c757d]"> has been successfully registered for Innovation Hackathon 2026.</span>
        </p>
      </div>

      {/* Main Content */}
      <div className="max-w-[840px] mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Registration Summary Card */}
        <div className="bg-white rounded-2xl border border-[rgba(212,211,241,0.6)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1)] overflow-hidden mb-8">
          {/* Gradient top border */}
          <div className="bg-gradient-to-r from-[#0058ab] to-[#00d5d0] h-1" />

          {/* Header */}
          <div className="border-b border-[rgba(212,211,241,0.4)] px-6 sm:px-8 py-5 flex items-center justify-between">
            <div>
              <h2 className="text-[#121a38] text-[17px] font-bold leading-[25px]">Registration Summary</h2>
              <p className="text-[#6c757d] text-sm">Here's a full breakdown of your team details</p>
            </div>
            <div className="bg-[rgba(0,213,208,0.1)] border border-[rgba(0,213,208,0.2)] rounded-full px-4 py-1.5 flex items-center gap-2">
              <div className="size-2 rounded-full bg-[#00d5d0]" />
              <span className="text-[#00828e] text-xs font-semibold">Confirmed</span>
            </div>
          </div>

          {/* Info Cards */}
          <div className="px-6 sm:px-8 pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-[rgba(0,88,171,0.05)] border border-[rgba(0,88,171,0.1)] rounded-[14px] p-4">
                <p className="text-[#6c757d] text-xs font-semibold mb-1">Team Name</p>
                <div className="flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10.6667 14V12.6667C10.6667 11.9594 10.3857 11.2811 9.88562 10.781C9.38553 10.281 8.70725 10 8.00001 10H4.00001C3.29277 10 2.61449 10.281 2.1144 10.781C1.6143 11.2811 1.33334 11.9594 1.33334 12.6667V14" stroke="#0058AB" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.00001 7.33333C7.47277 7.33333 8.66668 6.13943 8.66668 4.66667C8.66668 3.19391 7.47277 2 6.00001 2C4.52725 2 3.33334 3.19391 3.33334 4.66667C3.33334 6.13943 4.52725 7.33333 6.00001 7.33333Z" stroke="#0058AB" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14.6667 14V12.6667C14.6662 12.0758 14.4697 11.5019 14.1076 11.0349C13.7455 10.5679 13.2389 10.2344 12.6667 10.0867" stroke="#0058AB" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.6667 2.08667C11.2403 2.23354 11.7487 2.56714 12.1118 3.03488C12.475 3.50262 12.6719 4.07789 12.6719 4.67C12.6719 5.26211 12.475 5.83738 12.1118 6.30512C11.7487 6.77286 11.2403 7.10646 10.6667 7.25333" stroke="#0058AB" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p className="text-[#121a38] text-base font-bold">{data.teamName}</p>
                </div>
              </div>
              <div className="bg-[rgba(0,213,208,0.05)] border border-[rgba(0,213,208,0.2)] rounded-[14px] p-4">
                <p className="text-[#6c757d] text-xs font-semibold mb-1">Team Size</p>
                <p className="text-[#121a38]">
                  <span className="text-2xl font-bold">{totalMembers}</span>
                  <span className="text-[#6c757d] text-sm ml-1">members</span>
                </p>
              </div>
              <div className="bg-[rgba(254,177,0,0.05)] border border-[rgba(254,177,0,0.2)] rounded-[14px] p-4">
                <p className="text-[#6c757d] text-xs font-semibold mb-1">Event Date</p>
                <p className="text-[#121a38] text-base font-bold">Mar 25–27, 2026</p>
              </div>
            </div>

            {/* Team Leader */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="size-5 rounded bg-[#0058ab] flex items-center justify-center">
                  <span className="text-white text-xs font-bold">L</span>
                </div>
                <span className="text-[#121a38] text-sm font-semibold">Team Leader</span>
              </div>
              <div className="bg-[rgba(244,246,251,0.5)] border border-[rgba(212,211,241,0.6)] rounded-[14px] p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="size-11 rounded-full bg-[#0058ab] flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {data.teamLeader.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#121a38] text-base font-semibold">{data.teamLeader.name}</p>
                  <div className="flex items-center gap-1.5 text-[#6c757d] text-xs">
                    <Mail className="size-3" />
                    <span className="truncate">{data.teamLeader.email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="text-[#6c757d] text-xs">Grade</p>
                    <div className="bg-[rgba(0,88,171,0.1)] rounded-full px-2.5 py-0.5 mt-0.5">
                      <span className="text-[#0058ab] text-xs font-semibold">{data.leaderGrade}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#6c757d] text-xs">Role</p>
                    <p className="text-[#121a38] text-xs font-semibold mt-0.5">{data.leaderJobRole}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Members */}
            {data.teamMembers.length > 0 && (
              <div className="mb-6">
                <p className="text-[#121a38] text-sm font-semibold mb-3">Team Members ({data.teamMembers.length})</p>
                <div className="border border-[rgba(212,211,241,0.6)] rounded-[14px] overflow-hidden">
                  {data.teamMembers.map((member, i) => (
                    <div
                      key={member.id}
                      className={`flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-4 ${
                        i < data.teamMembers.length - 1 ? 'border-b border-[rgba(212,211,241,0.4)]' : ''
                      } ${i % 2 === 0 ? 'bg-white' : 'bg-[rgba(244,246,251,0.4)]'}`}
                    >
                      <div className="size-10 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0" style={{ backgroundColor: ['#1DB8F2', '#71609E', '#00828E', '#FEB100'][i % 4] }}>
                        {member.name.charAt(0).toLowerCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#121a38] text-sm font-semibold">{member.name}</p>
                        <div className="flex items-center gap-1 text-[#6c757d] text-xs">
                          <Mail className="size-3" />
                          <span className="truncate">{member.email}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="bg-[rgba(0,88,171,0.1)] rounded-full px-2 py-0.5">
                          <span className="text-[#0058ab] text-xs font-semibold">{member.grade}</span>
                        </div>
                        <span className="text-[#6c757d] text-xs">{member.jobRole}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Dispatch notice */}
                <div className="flex items-center gap-2 mt-4">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5.5" stroke="#00D5D0"/>
                    <path d="M4.5 6L5.5 7L7.5 5" stroke="#00D5D0" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-[#6c757d] text-xs">Invitations have been dispatched to all {data.teamMembers.length} members.</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
          <button
            onClick={() => {
              sessionStorage.removeItem('registrationData');
              navigate('/');
            }}
            className="flex items-center justify-center gap-2 bg-[#0058AB] hover:bg-[#004A94] text-white px-8 py-4 rounded-[14px] text-sm font-semibold transition-all shadow-[0px_10px_15px_0px_rgba(0,88,171,0.3)] cursor-pointer"
          >
            Go to My Hackathons
            <ArrowRight className="size-4" />
          </button>
          <button
            onClick={() => {}}
            className="flex items-center justify-center gap-2 bg-white border border-[#d4d3f1] text-[#4a5568] px-8 py-4 rounded-[14px] text-sm font-semibold transition-all hover:border-[#0058AB] hover:text-[#0058AB] cursor-pointer"
          >
            <ExternalLink className="size-4" />
            View Team Details
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-[#adb5bd] text-xs pb-8">
          <span>Capgemini Innovation Hub · Innovation Hackathon 2026</span>
          <span className="mx-2">›</span>
          <span>Registration ID: {regId}</span>
        </div>
      </div>
    </div>
    </DashboardLayout>
  );
}
